import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, from, map } from 'rxjs';
import { catchError, switchMap, timeout } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { OktaAuth } from '@okta/okta-auth-js';
import { OKTA_AUTH } from '@okta/okta-angular';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(
    private http: HttpClient,
    private router: Router,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
  ) {}

private getJwtPayload(token: string): any {
    try {
      // console.log(token);
      const payload = token.split('.')[1];
      // atob may throw if the payload is not base64url encoded
      return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch (e) {
      console.error('Failed to decode JWT payload', e);
      return {};
    }
  }

getAccessToken$(): Observable<string> {
    return from(this.oktaAuth.tokenManager.get('accessToken')).pipe(
      switchMap(token => {
        if (token && 'accessToken' in token) {
          const accessToken = token.accessToken as string;
          const jwtPayload = this.getJwtPayload(accessToken);
          // console.log(jwtPayload)
          const exp = jwtPayload.exp; // Expiry in seconds since epoch (UTC)
          const now = Math.floor(Date.now() / 1000);
          const leeway = 60; // seconds

          console.log('JWT exp:', exp, 'Current time:', now);

          if (exp && exp < (now + leeway)) {
            // Token is expired or about to expire, renew
            console.log('Token is expired or about to expire, renewing...');
            return from(this.oktaAuth.tokenManager.renew('accessToken')).pipe(
              switchMap(newToken => {
                if (newToken && 'accessToken' in newToken) {
                  return [newToken.accessToken as string];
                } else {
                  return throwError(() => new Error('No access token found after renewal!'));
                }
              })
            );
          } else {
            // console.log('Token is valid');
            return [accessToken as string];
          }
        } else {
          return throwError(() => new Error('No access token found!'));
        }
      })
    );
  }

  async logout(): Promise<void> {
    try {
      await this.oktaAuth.signOut({
        postLogoutRedirectUri:
          environment.okta.postLogoutRedirectUri
      });
    } catch (error) {
      console.error('Okta logout failed:', error);

      // Fallback: remove locally stored Okta tokens
      await this.oktaAuth.tokenManager.clear();

      window.location.assign(
        environment.okta.postLogoutRedirectUri
      );
    }
  }

// private getAccessToken$(): Observable<string> {
//   return from(this.oktaAuth.tokenManager.get('accessToken')).pipe(
//     switchMap(token => {
//       if (token && 'accessToken' in token) {
//         // Check if token is expired
//         const expiresAt = token.expiresAt;

//         console.log(Date.now());
//         const now = Math.floor(Date.now() / 1000);
//         console.log('Token expires at:', expiresAt, 'Current time:', now);
//         console.log("Token validation");

//         if (expiresAt && expiresAt < now) {
//           // Token is expired, try to renew
//           console.log("Token is expired, renewing...");
//           return from(this.oktaAuth.tokenManager.renew('accessToken')).pipe(
//             switchMap(newToken => {
//               if (newToken && 'accessToken' in newToken) {
//                 return [newToken.accessToken as string];
//               } else {
//                 return throwError(() => new Error('No access token found after renewal!'));
//               }
//             })
//           );
//         }
//         if (this.oktaAuth.tokenManager.hasExpired(token)) {

//           console.log("Token is not valid");
//           // Try to renew the token
//           return from(this.oktaAuth.tokenManager.renew('accessToken')).pipe(
//             switchMap(newToken => {
//               if (newToken && 'accessToken' in newToken) {
//                 return [newToken.accessToken as string];
//               } else {
//                 return throwError(() => new Error('No access token found after renewal!'));
//               }
//             })
//           );
//         } else {
//           console.log("Token is  valid");
//           return [token.accessToken as string];
//         }
//       } else {
//         return throwError(() => new Error('No access token found!'));
//       }
//     })
//   );
// }

    getCacheFile(filename: string, timeoutMs: number = 10000): Observable<any> {
      let file = `${filename}.json`; 

      const url = `${environment.cacheUrl}${file}`;

      return this.getAccessToken$().pipe(
      switchMap(accessToken => {
        const headers = new HttpHeaders({ 'Authorization': `Bearer ${accessToken}` });
        // const options: any = { headers, observe: 'response' }; 
        // return this.http.get(url, options).pipe(
        //   timeout(timeoutMs),
        //   catchError(this.handleHttpError)
        // );
        return this.http.get(url, { headers, observe: 'response' }).pipe(
        map(response => ({
          data: response.body,
          lastModified: response.headers.get('Last-Modified') || '2000-01-01T00:00:00Z'
        }))
      );
      })
    );

      // console.log(url);
  
      const headers = new HttpHeaders({
        'Authorization': environment.cacheToken
      });

      return this.http.get(url, { headers, observe: 'response' }).pipe(
        map(response => ({
          data: response.body,
          lastModified: response.headers.get('Last-Modified') || '2000-01-01T00:00:00Z'
        }))
      );
    }
  get(endpoint: string, params?: HttpParams, timeoutMs: number = 10000): Observable<any> {
  // get(endpoint: string, options: any = {}, timeoutMs: number = 10000): Observable<any> {
    return this.getAccessToken$().pipe(
      switchMap(accessToken => {
        const headers = new HttpHeaders({ 'Authorization': `Bearer ${accessToken}` });
        const options: any = { headers };
        if (params) options.params = params;
        return this.http.get(`${environment.apiUrl}${endpoint}`, options).pipe(
          timeout(timeoutMs),
          catchError(this.handleHttpError)
        );
      })
    );
  }

  formatDateMMDDYYYY(date: Date | null): string {
  if (!date) return '';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

  post(endpoint: string, body?: {}, timeoutMs: number = 10000): Observable<any> {
    return this.getAccessToken$().pipe(
      switchMap(accessToken => {
        const headers = new HttpHeaders({ 'Authorization': `Bearer ${accessToken}` });
        const options: any = { headers };
        return this.http.post(`${environment.apiUrl}${endpoint}`, body, options).pipe(
          timeout(timeoutMs),
          catchError(this.handleHttpError)
        );
      })
    );
  }

  private handleHttpError(err: any): Observable<never> {
    if (err.name === "HttpErrorResponse") {
      return throwError(() => new Error('Could not connect to the server. Please check your internet connection and try again later.'));
    } else if (err.name === 'TimeoutError') {
      return throwError(() => new Error('Request timed out!'));
    } else {
      return throwError(() => new Error('An error occurred on the server side. Please try again later.'));
    }
  }

  // DownloadFile operation,
  downloadFile(folderName: string, fileNameWithExt: string): Observable<Blob> {
  const url = `${environment.cacheUrl}/${folderName}/${fileNameWithExt}`;
  return this.getAccessToken$().pipe(
    switchMap(accessToken => {
      const headers = new HttpHeaders({ 
        'Authorization': `Bearer ${accessToken}` 
      });

      return this.http.get(url, { 
        headers, 
        responseType: 'blob' 
      });
    }));
  }
}